import { useEffect, useState } from "react";
import { listActiveChannels } from "@/lib/api";

/**
 * Tiny shared cache of the user's active channels so the Sidebar can
 * render quick links without each render firing its own API call.
 *
 * Pages that mutate the list (add / refresh / delete) should call
 * `invalidateActiveChannelsNav()` so the next subscriber pulls fresh
 * data. Mirrors the `useSavedKits` pattern used elsewhere.
 *
 * Cached shape: Array<{ channel_id, name, handle, thumbnail }>.
 * We deliberately keep this LEAN — the sidebar only needs the bare
 * minimum to render a clickable row, and the dashboard already
 * re-fetches the full rollup with stats when it mounts.
 */
const subscribers = new Set();
let cached = null;
let inFlight = null;

async function ensureLoaded() {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const rows = await listActiveChannels();
      cached = rows.map((r) => ({
        channel_id: r.channel_id,
        name: r.name || "Untitled",
        handle: r.handle || "",
        thumbnail: r.thumbnail || "",
      }));
    } catch {
      cached = [];
    }
    inFlight = null;
    return cached;
  })();
  return inFlight;
}

function notify() {
  subscribers.forEach((fn) => fn(cached ? [...cached] : []));
}

export function useActiveChannelsNav() {
  const [rows, setRows] = useState(cached ? [...cached] : []);
  useEffect(() => {
    let mounted = true;
    ensureLoaded().then((r) => { if (mounted) setRows([...r]); });
    subscribers.add(setRows);
    return () => {
      mounted = false;
      subscribers.delete(setRows);
    };
  }, []);
  return rows;
}

export function invalidateActiveChannelsNav() {
  cached = null;
  inFlight = null;
  // Best-effort: re-fetch now so subscribers see the new list without
  // waiting for the next mount.
  ensureLoaded().then(() => notify());
}
